import { Button, Card, Col, DatePicker, Form, Input, message, Popconfirm, Row, Select, Space } from 'antd';
import CustomReasonInput from "../common/CustomReasonInput";
import TextArea from 'antd/lib/input/TextArea';
import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { hideAuthLoader, showAuthLoader } from '../../appRedux/actions/Auth';
import Service from '../../service';
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined, CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import GuidedTour, { hasTourFlag } from '../common/GuidedTour/GuidedTour';

const TOUR_STORAGE_KEY = 'tour_seen_bill_reimbursement';
const TOUR_HINT_STORAGE_KEY = 'tour_hint_dismissed_bill_reimbursement';

const AddEditViewBillReimbursement = () => {
    const [form] = Form.useForm();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const { authUser } = useSelector(({ auth }) => auth);
    const { id } = useParams();

    const isViewMode = location.pathname.includes('/view/');
    const isEditMode = location.pathname.includes('/edit/');
    const isAddMode = !isViewMode && !isEditMode;

    const [reimbursementRows, setReimbursementRows] = useState([
        {
            id: Date.now(),
            date: null,
            claimType: '',
            amount: '',
            kms: '',
            newAttachments: [], // New files to upload
            existingAttachments: [], // Existing files from DB
            purpose: ''
        }
    ]);
    const [deletedAttachments, setDeletedAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isTourOpen, setIsTourOpen] = useState(false);
    const [isTourHintOpen, setIsTourHintOpen] = useState(false);

    // First-time visitors get a "click here" nudge pointing at the guide
    // trigger. It stays up until dismissed, which marks it done via its own
    // storage key so it never comes back.
    useEffect(() => {
      if (hasTourFlag(TOUR_STORAGE_KEY) || hasTourFlag(TOUR_HINT_STORAGE_KEY)) return;
        const showTimer = setTimeout(() => setIsTourHintOpen(true), 600);
        return () => clearTimeout(showTimer);
    }, []);

    const claimTypes = [
        'Petrol',
        'Food/Meal',
        'Miscellaneous',
        'Cab Exp.',
        'Training Certificate Fees',
        'Business Trip',
        'Mobile Bill',
        'Accommodation'
    ];
    const companySlug = localStorage.getItem("companyDomain");


    useEffect(() => {
        if (isEditMode || isViewMode) {
            fetchReimbursementData();
        }
    }, [id]);

    const fetchReimbursementData = async () => {
        try {
            dispatch(showAuthLoader());
            setLoading(true);

            const response = await Service.makeAPICall({
                methodName: Service.getMethod,
                api_url: Service.detail_reimbursement + '/' + id,
            });

            if (response && response.data && response.data.data) {
                const reimbursementDetails = response.data.data;

                form.setFieldsValue({
                    title: reimbursementDetails.title,
                    applicationDate: moment(reimbursementDetails.applicationDate || reimbursementDetails.date),
                    description: reimbursementDetails.description || '',
                });

                if (reimbursementDetails.entries && reimbursementDetails.entries.length > 0) {
                    const rows = reimbursementDetails.entries.map((item, index) => ({
                        id: Date.now() + index,
                        date: moment(item?.date),
                        claimType: item?.claimType || '',
                        amount: item?.amount || '',
                        kms: item?.kms || '',
                        newAttachments: [],
                        existingAttachments: item?.attachments || [], // Array of attachments
                        purpose: item?.purpose || '',
                    }));
                    setReimbursementRows(rows);
                }
            }

            dispatch(hideAuthLoader());
            setLoading(false);
        } catch (error) {
            console.log(error);
            dispatch(hideAuthLoader());
            setLoading(false);
            message.error('Failed to fetch reimbursement details');
        }
    };

    const addNewRow = () => {
        setReimbursementRows([
            ...reimbursementRows,
            {
                id: Date.now(),
                date: null,
                claimType: '',
                amount: '',
                kms: '',
                newAttachments: [],
                existingAttachments: [],
                purpose: ''
            }
        ]);
    };

    const deleteRow = (id, existingAttachments) => {
        if (reimbursementRows.length > 1) {
            // Add all existing attachments of this row to deleted list
            if (existingAttachments && existingAttachments.length > 0) {
                setDeletedAttachments([...deletedAttachments, ...existingAttachments]);
            }
            setReimbursementRows(reimbursementRows.filter(row => row.id !== id));
        } else {
            message.warning('At least one row is required');
        }
    };

    const handleRowChange = (id, field, value) => {
        setReimbursementRows(reimbursementRows.map(row => {
            if (row.id === id) {
                if (field === 'claimType') {
                    return { ...row, [field]: value, kms: '' };
                }
                return { ...row, [field]: value };
            }
            return row;
        }));
    };

    // Handle multiple file selection
    const handleFileChange = (id, event) => {
        const files = Array.from(event.target.files);
        if (files && files.length > 0) {
            setReimbursementRows(reimbursementRows.map(row => {
                if (row.id === id) {
                    // Append new files to existing newAttachments
                    return { ...row, newAttachments: [...row.newAttachments, ...files] };
                }
                return row;
            }));
        }
    };

    // Remove a new attachment (before upload)
    const removeNewAttachment = (rowId, fileIndex) => {
        setReimbursementRows(reimbursementRows.map(row => {
            if (row.id === rowId) {
                const updatedFiles = row.newAttachments.filter((_, idx) => idx !== fileIndex);
                return { ...row, newAttachments: updatedFiles };
            }
            return row;
        }));
    };

    // Remove an existing attachment (from DB)
    const removeExistingAttachment = (rowId, attachmentPath) => {
        setDeletedAttachments([...deletedAttachments, attachmentPath]);
        setReimbursementRows(reimbursementRows.map(row => {
            if (row.id === rowId) {
                const updatedAttachments = row.existingAttachments.filter(att => att !== attachmentPath);
                return { ...row, existingAttachments: updatedAttachments };
            }
            return row;
        }));
    };

    const calculateTotalAmount = () => {
        return reimbursementRows.reduce((total, row) => {
            const amount = parseFloat(row.amount) || 0;
            return total + amount;
        }, 0).toFixed(2);
    };

    const handleSubmit = async (values) => {
        try {
            const hasIncompleteRow = reimbursementRows.some(
                row => !row.date || !row.claimType || row.amount === '' || row.amount === null || row.amount === undefined
            );
            if (hasIncompleteRow) {
                message.error('Please fill Expense Date, Expense Type and Amount for all expense detail rows');
                return;
            }

            dispatch(showAuthLoader());

            const formData = new FormData();
            formData.append('title', values.title);
            formData.append('applicationDate', values.applicationDate.format('YYYY-MM-DD'));
            formData.append('createdBy', authUser?._id);
            formData.append('description', values.description || '');
            formData.append('totalAmount', calculateTotalAmount());

            console.log("=== FRONTEND: Preparing FormData with Multiple Attachments ===");

            // Add all reimbursement rows data
            reimbursementRows.forEach((row, index) => {
                console.log(`\nRow ${index}:`, {
                    date: row.date?.format('YYYY-MM-DD'),
                    claimType: row.claimType,
                    amount: row.amount,
                    kms: row.kms,
                    newAttachments: row.newAttachments.length,
                    existingAttachments: row.existingAttachments.length
                });

                formData.append(`reimbursements[${index}][date]`, row.date.format('YYYY-MM-DD'));
                formData.append(`reimbursements[${index}][claimType]`, row.claimType);
                formData.append(`reimbursements[${index}][amount]`, row.amount);
                formData.append(`reimbursements[${index}][purpose]`, row.purpose);

                // if (row.claimType === 'Petrol' && row.kms) {
                //     formData.append(`reimbursements[${index}][kms]`, row.kms);
                // }

                // Append multiple new files for this entry
                if (row.newAttachments && row.newAttachments.length > 0) {
                    row.newAttachments.forEach((file) => {
                        formData.append(`reimbursements[${index}][attachments]`, file);
                        console.log(`✓ Appended file for row ${index}: ${file.name}`);
                    });
                }

                // Send existing attachments as JSON string
                if (row.existingAttachments && row.existingAttachments.length > 0) {
                    formData.append(`reimbursements[${index}][existingAttachments]`, JSON.stringify(row.existingAttachments));
                    console.log(`✓ Existing attachments for row ${index}:`, row.existingAttachments.length);
                }
            });

            // Add deleted attachments if in edit mode
            if (isEditMode && deletedAttachments.length > 0) {
                formData.append('deletedAttachments', JSON.stringify(deletedAttachments));
                console.log('✓ Deleted attachments:', deletedAttachments.length);
            }

            // Debug: Log all FormData entries
            console.log("\n=== FormData Contents ===");
            for (let pair of formData.entries()) {
                if (pair[1] instanceof File) {
                    console.log(pair[0], '=', `[File: ${pair[1].name}, ${pair[1].size} bytes]`);
                } else {
                    console.log(pair[0], '=', pair[1]);
                }
            }

            const apiUrl = isEditMode
                ? `${Service.update_reimbursement}/${id}`
                : Service.add_reimbursement;

            const methodName = isEditMode ? Service.putMethod : Service.postMethod;

            console.log("\n=== Making API Call ===");
            console.log("URL:", apiUrl);
            console.log("Method:", methodName);

            const response = await Service.makeAPICall({
                methodName: methodName,
                api_url: apiUrl,
                body: formData,
            });

            console.log("\n=== API Response ===");
            console.log("Status:", response?.data?.status);
            console.log("Message:", response?.data?.message);

            if (response && response.data.status == 200) {
                message.success(isEditMode
                    ? 'Reimbursement updated successfully'
                    : 'Reimbursement added successfully'
                );
                history.push(`/${companySlug}/bill-reimbursement`)
            } else {
                message.error(response.message || `Failed to ${isEditMode ? 'update' : 'add'} reimbursement`);
            }

            dispatch(hideAuthLoader());
        } catch (error) {
            console.error("=== FRONTEND ERROR ===");
            console.error(error);
            dispatch(hideAuthLoader());
            message.error('Something went wrong. Please try again');
        }
    };

    const handleBack = () => {
        window.history.back();
        if (!isViewMode) history.push(`/${companySlug}/bill-reimbursement`);
    };

    const handleClear = () => {
        form.resetFields();
        setReimbursementRows([
            {
                id: Date.now(),
                date: null,
                claimType: '',
                amount: '',
                kms: '',
                newAttachments: [],
                existingAttachments: [],
                purpose: ''
            }
        ]);
        setDeletedAttachments([]);
    };

    const getTitle = () => {
        if (isViewMode) return 'View Reimbursement';
        if (isEditMode) return 'Edit Reimbursement';
        return 'Add Reimbursement';
    };

    const tourSteps = useMemo(
        () => [
            {
                target: '[data-tour="br-app-date"]',
                title: 'Always today',
                content: "This is fixed to the day you're applying - shown for reference, it isn't something you pick.",
            },
            {
                target: '[data-tour="br-expense-name"]',
                title: 'Name this claim',
                content: 'A short title for the whole claim, e.g. "September travel expenses".',
            },
            {
                target: '[data-tour="br-table"]',
                title: 'Add each expense line',
                content:
                    "One row per expense. Date, Expense Type and Amount are required on every row. For Expense Type, pick from the list or type your own and press Enter to add it. Attachments are optional and you can add more than one file per row. Click the + on the last row to add another expense, or the trash icon to remove one - at least one row is required.",
            },
            {
                target: '[data-tour="br-total"]',
                title: 'Adds up automatically',
                content: "This is the sum of every row's Amount above - you can't edit it directly.",
            },
            {
                target: '[data-tour="br-description"]',
                title: 'Extra notes',
                content: 'Optional - anything else worth telling whoever approves this.',
            },
            {
                target: '[data-tour="br-submit-btn"]',
                title: 'Submit',
                content: 'Sends this claim for approval.',
            },
        ],
        []
    );

    const hintSteps = useMemo(
        () => [
            {
                target: '[data-tour="br-guide-icon"]',
                shape: 'circle',
                title: 'New here?',
                content: 'Click this icon anytime for a quick, step-by-step guide to this form.',
            },
        ],
        []
    );

    if (loading) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    Loading...
                </div>
            </Card>
        );
    }

    return (
        <Card className="employee-card">
            <div className='inout-header text-left'>
                <h2 style={{ margin: 0 }}>
                    {getTitle()}{' '}
                    <QuestionCircleOutlined
                        data-tour="br-guide-icon"
                        className="guide-title-icon"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            setIsTourHintOpen(false);
                            setIsTourOpen(true);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setIsTourHintOpen(false);
                                setIsTourOpen(true);
                            }
                        }}
                        title="Click for a step-by-step guide"
                    />
                </h2>
                <Button type="primary" icon={<ArrowLeftOutlined />} onClick={handleBack}>
                    Back
                </Button>
            </div>
            <Card className="main-content-wrapper">

                <Form form={form} className='modal-form-col' onFinish={handleSubmit} layout="vertical">
                    <Row gutter={[24,24]} style={{ marginBottom: 24 }}>
                        <Col xs={24} md={12} data-tour="br-app-date">
                            <Form.Item
                                label="Application Date"
                                name="applicationDate"
                                initialValue={moment()}
                                rules={[{ required: !isViewMode, message: 'Please select date' }]}
                            >
                                {/* Always the day the claim is raised - shown for
                                    reference, never picked. */}
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD-MM-YYYY"
                                    disabled
                                    allowClear={false}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12} data-tour="br-expense-name">
                            <Form.Item
                                label="Expense Name"
                                name="title"
                                rules={[{ required: !isViewMode, message: 'Please enter Expense Name' }]}
                            >
                                <Input placeholder="Enter Expense Name" disabled={isViewMode} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div style={{ marginBottom: 16 }}>
                        <h3>Expense Details</h3>
                    </div>

                    <div style={{ overflowX: 'auto' }} data-tour="br-table">
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f0f0f0' }}>
                                    <th style={{ padding: 8, border: '1px solid #d9d9d9', minWidth: 60 }}>Sr.No</th>
                                    <th style={{ padding: 8, border: '1px solid #d9d9d9', minWidth: 130 }}>Expense Date</th>
                                    <th style={{ padding: 8, border: '1px solid #d9d9d9', minWidth: 150 }}>Expense Type</th>
                                    <th style={{ padding: 8, border: '1px solid #d9d9d9', minWidth: 100 }}>Amount</th>
                                    <th style={{ padding: 8, border: '1px solid #d9d9d9', minWidth: 250 }}>Attachments</th>
                                    <th style={{ padding: 8, border: '1px solid #d9d9d9', minWidth: 180 }}>Description</th>
                                    {!isViewMode && (
                                        <>
                                            <th style={{ padding: 8, border: '1px solid #d9d9d9', minWidth: 80 }}>Add</th>
                                            <th style={{ padding: 8, border: '1px solid #d9d9d9', minWidth: 80 }}>Delete</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {reimbursementRows.map((row, index) => (
                                    <tr key={row.id}>
                                        <td style={{ padding: 8, border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                            {index + 1}
                                        </td>
                                        <td style={{ padding: 8, border: '1px solid #d9d9d9' }}>
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                format="DD-MM-YYYY"
                                                value={row.date}
                                                onChange={(date) => handleRowChange(row.id, 'date', date)}
                                                disabled={isViewMode}
                                            />
                                        </td>
                                        <td style={{ padding: 8, border: '1px solid #d9d9d9' }}>
                                            {isViewMode ? (
                                                <Input value={row.claimType} disabled />
                                            ) : (
                                                <CustomReasonInput
                                                    value={row.claimType || undefined}
                                                    placeholder="Select or type expense type"
                                                    onChange={(value) => handleRowChange(row.id, 'claimType', value)}
                                                    options={claimTypes.map((type) => ({ value: type, label: type }))}
                                                />

                                            )}
                                        </td>
                                        <td style={{ padding: 8, border: '1px solid #d9d9d9' }}>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={row.amount}
                                                onChange={(e) => handleRowChange(row.id, 'amount', e.target.value)}
                                                placeholder="Enter amount"
                                                disabled={isViewMode}
                                            />
                                        </td>
                                        <td style={{ padding: 8, border: '1px solid #d9d9d9' }}>
                                            {/* Display existing attachments */}
                                            {row.existingAttachments && row.existingAttachments.length > 0 && (
                                                <div style={{ marginBottom: 8 }}>
                                                    {row.existingAttachments.map((att, attIdx) => (
                                                        <div key={attIdx} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            marginTop: 4,
                                                            padding: '4px',
                                                            backgroundColor: '#f5f5f5',
                                                            borderRadius: '4px'
                                                        }}>
                                                            <a
                                                                href={`${process.env.REACT_APP_API_URL}/uploads/claims/${att}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ fontSize: '11px', flex: 1 }}
                                                            >
                                                                📎 {att.split('/').pop().substring(0, 15)}...
                                                            </a>
                                                            {!isViewMode && (
                                                                <Button
                                                                    size="small"
                                                                    danger
                                                                    type="link"
                                                                    icon={<CloseOutlined />}
                                                                    onClick={() => removeExistingAttachment(row.id, att)}
                                                                    style={{ padding: '0 4px' }}
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Display new attachments */}
                                            {row.newAttachments && row.newAttachments.length > 0 && (
                                                <div style={{ marginBottom: 8 }}>
                                                    {row.newAttachments.map((file, fileIdx) => (
                                                        <div key={fileIdx} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            marginTop: 4,
                                                            padding: '4px',
                                                            backgroundColor: '#f5f5f5',
                                                            borderRadius: '4px'
                                                        }}>
                                                            <span style={{ fontSize: '11px', flex: 1 }}>
                                                                📎 {file.name.substring(0, 15)}...
                                                            </span>
                                                            {!isViewMode && (
                                                                <Button
                                                                    size="small"
                                                                    danger
                                                                    type="link"
                                                                    icon={<CloseOutlined />}
                                                                    onClick={() => removeNewAttachment(row.id, fileIdx)}
                                                                    style={{ padding: '0 4px' }}
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* File input for adding more attachments */}
                                            {!isViewMode && (
                                                <input
                                                    type="file"
                                                    multiple
                                                    onChange={(e) => handleFileChange(row.id, e)}
                                                    style={{ width: '100%', fontSize: '11px' }}
                                                />
                                            )}
                                        </td>
                                        <td style={{ padding: 8, border: '1px solid #d9d9d9' }}>
                                            <TextArea
                                                rows={2}
                                                value={row?.purpose}
                                                onChange={(e) => handleRowChange(row.id, 'purpose', e.target.value)}
                                                placeholder="Enter purpose"
                                                disabled={isViewMode}
                                            />
                                        </td>
                                        {!isViewMode && (
                                            <>
                                                <td style={{ padding: 8, border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                    {index === reimbursementRows.length - 1 && (
                                                        <Button
                                                            shape="circle"
                                                            icon={<PlusOutlined />}
                                                            onClick={addNewRow}
                                                        />
                                                    )}
                                                </td>
                                                <td style={{ padding: 8, border: '1px solid #d9d9d9', textAlign: 'center' }}>
                                                    <Popconfirm
                                                        title="Remove this expense row?"
                                                        description="This action cannot be undone."
                                                        okText="Remove"
                                                        okButtonProps={{ danger: true }}
                                                        cancelText="Cancel"
                                                        onConfirm={() => deleteRow(row?.id, row?.existingAttachments)}
                                                        disabled={reimbursementRows.length === 1}
                                                    >
                                                        <Button
                                                            danger
                                                            shape="circle"
                                                            icon={<DeleteOutlined />}
                                                            disabled={reimbursementRows.length === 1}
                                                        />
                                                    </Popconfirm>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col xs={24} md={12} data-tour="br-total">
                            <div style={{
                                padding: 16,
                                border: '1px solid #d9d9d9',
                                borderRadius: 6,
                                backgroundColor: '#fafafa'
                            }}>
                                <strong>Total Amount: ₹{calculateTotalAmount()}</strong>
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={24} data-tour="br-description">
                            <Form.Item
                                label="Additional Notes / Description"
                                name="description"
                            >
                                <TextArea
                                    rows={4}
                                    placeholder="Enter any additional notes"
                                    disabled={isViewMode}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row justify="center">
                        <Space size="large">
                            {!isViewMode && (
                                <>
                                    <Button type="primary" htmlType="submit" data-tour="br-submit-btn">
                                        {isEditMode ? 'Update' : 'Apply'}
                                    </Button>
                                    <Button onClick={handleClear}>
                                        Clear
                                    </Button>
                                </>
                            )}
                        </Space>
                    </Row>
                </Form>
            </Card>

            {isTourOpen && <GuidedTour steps={tourSteps} onClose={() => setIsTourOpen(false)} storageKey={TOUR_STORAGE_KEY} />}

            {!isTourOpen && isTourHintOpen && (
                <GuidedTour steps={hintSteps} onClose={() => setIsTourHintOpen(false)} storageKey={TOUR_HINT_STORAGE_KEY} />
            )}
        </Card>
    );
};

export default AddEditViewBillReimbursement;